
import { useState, useEffect, useCallback, useRef } from 'react';
import { useEngagementState } from './useEngagementState';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import confetti from 'canvas-confetti';

const COOLDOWN_KEY = 'engagement_popup_last_shown';
const DAILY_COUNT_KEY = 'engagement_popup_daily_count';
const SESSION_SHOWN_KEY = 'engagement_popup_session_shown';

interface EngagementPopupData {
  type: 'praise' | 'encouragement' | 'milestone';
  title: string;
  message: string;
  style: 'cosmic-horror' | 'analog-horror' | 'corrupted-transmission';
}

const getDailyKey = () => {
  const today = new Date().toISOString().split('T')[0];
  return `${DAILY_COUNT_KEY}_${today}`;
};

const getSessionShown = () => sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true';
const markSessionShown = () => sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');

const getDailyCount = (): number => {
  const count = localStorage.getItem(getDailyKey());
  return count ? parseInt(count, 10) : 0;
};

const incrementDailyCount = () => {
  localStorage.setItem(getDailyKey(), String(getDailyCount() + 1));
};

const canShowPopup = (): boolean => {
  if (getSessionShown()) return false;
  if (getDailyCount() >= 2) return false;
  
  const lastShown = localStorage.getItem(COOLDOWN_KEY);
  if (lastShown) {
    const hoursSince = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60);
    if (hoursSince < 4) return false;
  }
  
  return true;
};

const markPopupShown = () => {
  localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
  incrementDailyCount();
  markSessionShown();
};

const PRAISE_MESSAGES = [
  { title: "⚡ SIGNAL AMPLIFIED", message: "Your consistency is generating power levels we haven't seen before. The void watches with admiration." },
  { title: "🌌 COSMIC RESONANCE DETECTED", message: "Your streak has created a resonance pattern across the data streams. You are becoming something greater." },
  { title: "✨ TRANSMISSION RECEIVED", message: "The frequencies align. Your dedication has not gone unnoticed by the observers beyond." },
  { title: "🔮 PATTERN RECOGNIZED", message: "The monolith hums with approval. Your data signature grows stronger with each cycle." },
  { title: "⭐ THRESHOLD BREACHED", message: "You've crossed into a higher plane of consistency. The ancient algorithms celebrate your ascent." },
];

const ENCOURAGEMENT_MESSAGES = [
  { title: "📡 FAINT SIGNAL", message: "We detected a gap in your data stream. The void misses your input. Even a whisper would echo beautifully." },
  { title: "🌑 THE QUIET BETWEEN", message: "Silence has its place, but the pattern waits for you. One small step can restart the signal." },
  { title: "👁 STILL WATCHING", message: "The observers haven't forgotten you. No judgment, only patience. Return when you're ready." },
  { title: "🌫 THROUGH THE STATIC", message: "Sometimes the signal fades. That's okay. We're here, tuned to your frequency, whenever you choose to transmit." },
];

const MILESTONE_MESSAGES = [
  { title: "🏆 STREAK ANOMALY", message: "Your streak has reached levels that defy normal patterns. The transmission logs are overflowing." },
  { title: "💎 DATA CRYSTALLIZED", message: "Consistent input has crystallized into something remarkable. Your journey defies entropy." },
];

export const useEngagementPopups = () => {
  const { engagementState, isLoading } = useEngagementState();
  const [popup, setPopup] = useState<EngagementPopupData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasChecked = useRef(false);

  const triggerHaptic = useCallback(async () => {
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch {
      // Haptics not available (web browser)
    }
  }, []);

  const fireConfetti = useCallback(() => {
    confetti({
      particleCount: 30,
      spread: 55,
      origin: { y: 0.6 },
      colors: ['#a855f7', '#6366f1', '#f59e0b', '#ec4899'],
      disableForReducedMotion: true,
    });
  }, []);

  const showPopup = useCallback((data: EngagementPopupData) => {
    setPopup(data);
    setIsVisible(true);
    markPopupShown();
    triggerHaptic();
    
    if (data.type === 'praise' || data.type === 'milestone') {
      setTimeout(fireConfetti, 300);
    }
  }, [triggerHaptic, fireConfetti]);

  const dismissPopup = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => setPopup(null), 300);
  }, []);

  // Check engagement state and show appropriate popup
  useEffect(() => {
    if (isLoading || !engagementState || hasChecked.current) return;
    hasChecked.current = true;

    if (!canShowPopup()) return;

    const { moodState, engagementScore, streakInfo } = engagementState;

    // Check for milestone first (streak >= 7)
    if (streakInfo.login_streak >= 7 && streakInfo.login_streak % 7 === 0) {
      const msg = MILESTONE_MESSAGES[Math.floor(Math.random() * MILESTONE_MESSAGES.length)];
      showPopup({
        type: 'milestone',
        title: msg.title,
        message: `${msg.message}\n\n🔥 ${streakInfo.login_streak}-day streak!`,
        style: 'corrupted-transmission',
      });
      return;
    }

    // Praise for high engagement
    if ((moodState === 'thriving' || moodState === 'steady') && engagementScore > 70) {
      const msg = PRAISE_MESSAGES[Math.floor(Math.random() * PRAISE_MESSAGES.length)];
      showPopup({
        type: 'praise',
        title: msg.title,
        message: msg.message,
        style: 'cosmic-horror',
      });
      return;
    }

    // Encouragement for low engagement
    if (['struggling', 'at_risk', 'churning'].includes(moodState)) {
      const msg = ENCOURAGEMENT_MESSAGES[Math.floor(Math.random() * ENCOURAGEMENT_MESSAGES.length)];
      showPopup({
        type: 'encouragement',
        title: msg.title,
        message: msg.message,
        style: 'analog-horror',
      });
      return;
    }
  }, [isLoading, engagementState, showPopup]);

  return {
    popup,
    isVisible,
    dismissPopup,
  };
};
